import api from "./api";
import { getBackendUrl } from "../config";

// Preparacion de SDP exclusivamente simulada. Sin renovacion JWT ni reintentos.
const setting = name => window.ENV !== undefined
  ? window.ENV[name] || ""
  : import.meta.env[name] || "";

const origin = value => {
  try {
    if (typeof value !== "string" || !value || value.trim() !== value) return null;
    const url = new URL(value);
    const host = url.hostname.toLowerCase().replace(/\.$/, "");
    if (url.username || url.password || url.search || url.hash || url.pathname !== "/") return null;
    if (host === "api.mandaexpress.com.mx") return null;
    if (url.protocol !== "https:" && !(url.protocol === "http:" &&
      ["localhost", "127.0.0.1", "[::1]"].includes(host))) return null;
    return url.origin;
  } catch (_) { return null; }
};

const configuredOrigin = () => {
  if (setting("VITE_META_CALL_ACCEPT_MOCK_UI_ENABLED") !== "true") return null;
  const pinned = origin(setting("VITE_META_CALL_STAGING_API_ORIGIN"));
  return pinned && pinned === origin(getBackendUrl()) &&
    pinned === origin(api.defaults.baseURL) ? pinned : null;
};

export const isMetaCallAcceptMockConfigured = () => Boolean(
  configuredOrigin() && window.isSecureContext && window.RTCPeerConnection &&
  window.AbortController && window.fetch && navigator.mediaDevices?.getUserMedia
);

const readToken = () => {
  try {
    const token = JSON.parse(localStorage.getItem("token"));
    return typeof token === "string" && token.length > 0 && !/[\r\n]/.test(token)
      ? token : null;
  } catch (_) { return null; }
};

export const startMetaCallAcceptMock = ({ callId, expiresAtMs, isCurrent }) => {
  const controller = new AbortController();
  let stream;
  let peer;
  let stopped = false;
  let timer;
  let watchdog;
  const cleanup = () => {
    if (stream) stream.getTracks().forEach(track => track.stop());
    if (peer) peer.close();
    clearTimeout(timer);
    clearInterval(watchdog);
  };
  const cancel = () => {
    stopped = true;
    controller.abort();
    cleanup();
  };
  const token = readToken();
  const base = configuredOrigin();
  const check = () => {
    if (stopped || !isCurrent() || Date.now() >= expiresAtMs ||
        !token || readToken() !== token || configuredOrigin() !== base) {
      cancel();
      throw new Error("META_CALL_CANCELLED");
    }
  };
  // Also settles when a microphone permission dialog is left unanswered.
  const untilCancelled = promise => new Promise((resolve, reject) => {
    const abort = () => reject(new Error("META_CALL_CANCELLED"));
    if (controller.signal.aborted) { abort(); return; }
    controller.signal.addEventListener("abort", abort, { once: true });
    promise.then(resolve, reject).finally(() =>
      controller.signal.removeEventListener("abort", abort));
  });
  const request = async (suffix, body) => {
    check();
    const response = await fetch(base + "/meta/calls/" + encodeURIComponent(callId) + suffix, {
      method: body === undefined ? "GET" : "POST",
      headers: {
        Authorization: "Bearer " + token,
        ...(body === undefined ? {} : { "Content-Type": "application/json" })
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      credentials: "include", cache: "no-store", redirect: "error",
      signal: controller.signal
    });
    check();
    if (!response.ok) throw new Error("META_CALL_HTTP_" + response.status);
    const data = await response.json();
    check();
    return { status: response.status, data };
  };
  const run = async () => {
    try {
      if (!isMetaCallAcceptMockConfigured() || !base || !token ||
          typeof callId !== "string" || !callId || callId.length > 256 ||
          callId.trim() !== callId || /[\x00-\x1f\x7f]/.test(callId) ||
          !Number.isFinite(expiresAtMs)) throw new Error("META_CALL_DISABLED");
      check();
      timer = setTimeout(cancel, 30000);
      watchdog = setInterval(() => { try { check(); } catch (_) { /* cancelled */ } }, 100);
      const claim = await request("/claim", {});
      if (claim.status !== 200 || claim.data?.status !== "reserved")
        throw new Error("META_CALL_CLAIM_UNAVAILABLE");
      const offer = await request("/offer");
      if (offer.status !== 200 || offer.data?.sdp_type !== "offer" ||
          typeof offer.data.sdp !== "string" || !offer.data.sdp.startsWith("v=0") ||
          new TextEncoder().encode(offer.data.sdp).length > 262144)
        throw new Error("META_CALL_OFFER_INVALID");

      // Relay-only with NO TURN server: deliberately no usable ICE path.
      // This is a negotiation mock, never a transport for a real call.
      peer = new RTCPeerConnection({ iceServers: [], iceTransportPolicy: "relay" });
      await untilCancelled(peer.setRemoteDescription({ type: "offer", sdp: offer.data.sdp }));
      check();
      const microphone = navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      // A late permission grant must not leave a microphone open after cancellation.
      const capture = microphone.then(value => {
        if (stopped) { value.getTracks().forEach(track => track.stop()); throw new Error("META_CALL_CANCELLED"); }
        stream = value;
        return value;
      });
      await untilCancelled(capture);
      check();
      const tracks = stream.getAudioTracks();
      if (!tracks.length) throw new Error("META_CALL_NO_AUDIO_TRACK");
      tracks.forEach(track => { track.enabled = false; peer.addTrack(track, stream); });
      const answer = await untilCancelled(peer.createAnswer());
      check();
      await untilCancelled(peer.setLocalDescription(answer));
      check();
      if (peer.iceGatheringState !== "complete") {
        await untilCancelled(new Promise(resolve => {
          peer.onicegatheringstatechange = () => {
            if (peer.iceGatheringState === "complete") resolve();
          };
          if (peer.iceGatheringState === "complete") resolve();
        }));
      }
      check();
      const local = peer.localDescription;
      if (!local || local.type !== "answer" || typeof local.sdp !== "string" ||
          new TextEncoder().encode(local.sdp).length > 262144)
        throw new Error("META_CALL_ANSWER_INVALID");
      const result = await request("/accept", { session: { sdp_type: "answer", sdp: local.sdp } });
      if (result.status !== 202 || result.data?.status !== "reserved_test" ||
          result.data.provider_contacted !== false || result.data.audio_connected !== false)
        throw new Error("META_CALL_UNCONFIRMED");
      return "reserved_test";
    } finally { stopped = true; cleanup(); }
  };
  return { cancel, promise: run() };
};